import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { KioskProvider } from '@/context/KioskContext';
import { PageLoadingFallback } from './PageLoadingFallback';

const PublicProfile = lazy(() => import('@/pages/PublicProfile'));
const ItemDetail = lazy(() => import('@/pages/ItemDetail'));

/**
 * Watches the location and forces visitors back to `/` if they somehow
 * navigate outside the current store's kiosk scope. Belt-and-suspenders
 * guard for any internal link we missed.
 */
function KioskGuard({ ownerUsername }: { ownerUsername: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const allowedPrefix = `/s/${ownerUsername}`;
    if (!location.pathname.startsWith(allowedPrefix)) {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, ownerUsername]);
  return null;
}

/**
 * Entry point for `/s/:username/*`. Renders the storefront (and optional item
 * detail) inside a kiosk-locked shell. Any other sub-path bounces to `/`.
 * The kiosk "you're viewing @username's store" disclaimer lives in the
 * shared Header (auto-detected via useKiosk).
 */
export function KioskShell() {
  const { username } = useParams<{ username: string }>();

  if (!username) return <Navigate to="/" replace />;

  return (
    <KioskProvider ownerUsername={username}>
      <KioskGuard ownerUsername={username} />
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route index element={<PublicProfile />} />
          <Route path="item/:id" element={<ItemDetail />} />
          <Route path="*" element={<Navigate to={`/s/${username}`} replace />} />
        </Routes>
      </Suspense>
    </KioskProvider>
  );
}

